import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useColorPalette } from '../../contexts/ColorPaletteContext';
import tvTheme, { tvColLabelColor, tvTextStroke } from './tvTheme';
import { normalizeHeatmapMatrix } from '../../utils/tvStationHeatmap';

/**
 * 天気予報番組風ヒートマップ
 * - stickyDateHeader: 日付行を上に固定して時間だけ縦スクロール
 * - fillWidth: 列を親幅いっぱいに広げる（週間7日向け）
 */
function TVHeatmapGrid({
  title,
  rowHeader = '',
  colHeader = '',
  rowLabels,
  colLabels,
  cells,
  compact = false,
  showValues = true,
  stickyDateHeader = false,
  fillWidth = false,
  sizePreset = 'default',
  fitContent = false,
  centerGrid = false,
}) {
  const { getCellColor, getTextColor } = useColorPalette();
  const safeRowLabels = rowLabels ?? [];
  const safeColLabels = colLabels ?? [];
  const normalizedCells = useMemo(
    () => normalizeHeatmapMatrix(safeRowLabels, safeColLabels, cells),
    [safeRowLabels, safeColLabels, cells],
  );
  const size = (() => {
    if (sizePreset === 'stationOverlayWeek') {
      return tvTheme.heatmap.stationOverlayWeek;
    }
    if (sizePreset === 'pastWeekOverview') {
      return compact ? tvTheme.heatmap.pastWeekOverview : {
        ...tvTheme.heatmap.pastWeekOverview,
        labelW: 112,
        headerH: 36,
        cellH: 48,
        cellMinW: 42,
        valueFont: 24,
        labelFont: 14,
        headerFont: 15,
      };
    }
    if (sizePreset === 'stationOverlay') {
      return compact ? tvTheme.heatmap.stationOverlay : tvTheme.heatmap.stationOverlayFull;
    }
    if (sizePreset === 'station') {
      return compact ? tvTheme.heatmap.station : tvTheme.heatmap.stationFull;
    }
    return compact ? tvTheme.heatmap.compact : tvTheme.heatmap.full;
  })();

  const gridColumns = useMemo(() => {
    const colPart = fillWidth
      ? `repeat(${safeColLabels.length}, minmax(${size.cellMinW}px, 1fr))`
      : `repeat(${safeColLabels.length}, ${size.cellMinW}px)`;
    return `${size.labelW}px ${colPart}`;
  }, [safeColLabels.length, fillWidth, size.cellMinW, size.labelW]);

  const gridMinWidth = size.labelW + safeColLabels.length * size.cellMinW;

  const renderColHeaderCell = (label, ci) => (
    <Box
      key={`col-h-${label}-${ci}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: tvTheme.colors.headerBar,
        borderRight: `2px solid ${tvTheme.colors.gridLine}`,
        borderBottom: `2px solid ${tvTheme.colors.gridLine}`,
        minHeight: size.headerH,
      }}
    >
      <Typography
        sx={{
          fontSize: size.headerFont,
          fontWeight: 700,
          color: tvColLabelColor(label),
          lineHeight: 1.1,
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const renderDataCell = (rowLabel, ri, colLabel, ci) => {
    const value = normalizedCells[ri]?.[ci] ?? 0;
    const bg = value === 0 ? '#e8eef3' : getCellColor(value);
    const fg = value === 0 ? '#5a6a7a' : getTextColor(value);

    return (
      <Box
        key={`cell-${rowLabel}-${colLabel}-${ci}`}
        sx={{
          minHeight: size.cellH,
          bgcolor: bg,
          color: fg === 'inherit' ? '#1a202c' : fg,
          borderRight: `2px solid ${tvTheme.colors.gridLine}`,
          borderBottom: ri < safeRowLabels.length - 1 ? `2px solid ${tvTheme.colors.gridLine}` : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        {showValues && (
          <Typography sx={{ fontSize: size.valueFont, fontWeight: 900, lineHeight: 1 }}>
            {value === 0 ? '−' : value}
          </Typography>
        )}
      </Box>
    );
  };

  const cornerCell = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: tvTheme.colors.labelBar,
        borderRight: `2px solid ${tvTheme.colors.gridLine}`,
        borderBottom: `2px solid ${tvTheme.colors.gridLine}`,
        minHeight: size.headerH,
        position: stickyDateHeader ? 'sticky' : undefined,
        left: stickyDateHeader ? 0 : undefined,
        zIndex: stickyDateHeader ? 4 : undefined,
      }}
    >
      <Typography
        sx={{ fontSize: size.headerFont, fontWeight: 800, color: '#fff', ...tvTextStroke() }}
      >
        {rowHeader}
      </Typography>
    </Box>
  );

  const rowLabelCell = (label, ri) => (
    <Box
      key={`row-label-${label}-${ri}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: tvTheme.colors.labelBar,
        borderRight: `2px solid ${tvTheme.colors.gridLine}`,
        borderBottom: ri < safeRowLabels.length - 1 ? `2px solid ${tvTheme.colors.gridLine}` : 'none',
        minHeight: size.cellH,
        position: 'sticky',
        left: 0,
        zIndex: 2,
        boxShadow: '2px 0 6px rgba(0,0,0,0.1)',
      }}
    >
      <Typography
        sx={{
          fontSize: size.labelFont,
          fontWeight: 800,
          color: '#fff',
          lineHeight: 1.15,
          textAlign: 'center',
          ...tvTextStroke(),
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: fitContent ? '0 0 auto' : 1,
      }}
    >
      {title && (
        <Typography
          sx={{
            fontSize: size.headerFont,
            fontWeight: 800,
            color: tvTheme.colors.textDark,
            mb: 0.35,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
      )}

      <Box
        sx={{
          flex: fitContent ? '0 0 auto' : 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: fitContent ? 'none' : tvTheme.colors.panelBorder,
          borderRadius: fitContent ? 0 : compact ? '6px' : '8px',
          bgcolor: fitContent ? 'transparent' : tvTheme.colors.panelGlass,
          boxShadow: fitContent ? 'none' : 'inset 0 0 0 1px rgba(13,74,143,0.12), 0 2px 10px rgba(13,74,143,0.12)',
          overflow: 'hidden',
        }}
      >
        {stickyDateHeader ? (
          <>
            {/* 日付行：縦スクロールの外（上部固定） */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                width: '100%',
                flexShrink: 0,
                minWidth: fillWidth ? undefined : gridMinWidth,
              }}
            >
              {cornerCell}
              {safeColLabels.map(renderColHeaderCell)}
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridColumns,
                  width: '100%',
                  minWidth: fillWidth ? undefined : gridMinWidth,
                }}
              >
                {safeRowLabels.map((rowLabel, ri) => (
                  <React.Fragment key={`row-${rowLabel}-${ri}`}>
                    {rowLabelCell(rowLabel, ri)}
                    {safeColLabels.map((colLabel, ci) => renderDataCell(rowLabel, ri, colLabel, ci))}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: fitContent ? '0 0 auto' : 1,
              minHeight: 0,
              overflow: fitContent ? 'visible' : 'auto',
              WebkitOverflowScrolling: 'touch',
              display: 'flex',
              justifyContent: centerGrid ? 'center' : 'flex-start',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                width: fillWidth ? '100%' : undefined,
                minWidth: fillWidth ? undefined : gridMinWidth,
                maxWidth: fillWidth ? '100%' : undefined,
                mx: centerGrid ? 'auto' : undefined,
              }}
            >
              {cornerCell}
              {safeColLabels.map(renderColHeaderCell)}
              {safeRowLabels.map((rowLabel, ri) => (
                <React.Fragment key={`row-${rowLabel}-${ri}`}>
                  {rowLabelCell(rowLabel, ri)}
                  {safeColLabels.map((colLabel, ci) => renderDataCell(rowLabel, ri, colLabel, ci))}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {colHeader && (
        <Typography
          sx={{
            mt: 0.35,
            fontSize: size.headerFont,
            fontWeight: 700,
            color: tvTheme.colors.textDark,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {colHeader}
        </Typography>
      )}
    </Box>
  );
}

TVHeatmapGrid.propTypes = {
  title: PropTypes.string,
  rowHeader: PropTypes.string,
  colHeader: PropTypes.string,
  rowLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  colLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  cells: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  compact: PropTypes.bool,
  showValues: PropTypes.bool,
  stickyDateHeader: PropTypes.bool,
  fillWidth: PropTypes.bool,
  sizePreset: PropTypes.oneOf([
    'default',
    'station',
    'stationOverlay',
    'stationOverlayFull',
    'stationOverlayWeek',
    'pastWeekOverview',
  ]),
  fitContent: PropTypes.bool,
  centerGrid: PropTypes.bool,
};

export default TVHeatmapGrid;
